import { useContext, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
  alpha,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import axiosInstance from '../../lib/axios';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useSnackbar } from '../../components/SnackbarProvider';
import { UpdatePricingTierDto, TierName, DiscountType, PricingTier } from '../../types/b2b';
import { User, Product } from '../../types';

interface FormData {
  tierName: TierName;
  minimumQuantity: number;
  maximumQuantity: number | null;
  discountType: DiscountType;
  discountValue: number;
  effectiveFrom: string;
  effectiveTo: string;
  isActive: boolean;
  priority: number;
}

const fetchPricingTier = async (id: string): Promise<PricingTier> => {
  const response = await axiosInstance.get(`/pricing-tiers/${id}`);
  return response.data;
};

const fetchCustomers = async (): Promise<User[]> => {
  const response = await axiosInstance.get('/users?role=customer&limit=100');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchProducts = async (): Promise<Product[]> => {
  const response = await axiosInstance.get('/products?limit=100');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

export const PricingTierEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mode } = useContext(ColorModeContext);
  const { showMessage } = useSnackbar();
  const animations = useAnimationStyles();

  const [selectedCustomer, setSelectedCustomer] = useState<User | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const { data: pricingTier, isLoading: tierLoading } = useQuery({
    queryKey: ['pricing-tier', id],
    queryFn: () => fetchPricingTier(id!),
    enabled: !!id,
  });

  const { data: customers, isLoading: customersLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: fetchCustomers,
  });

  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts,
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      tierName: 'bronze',
      minimumQuantity: 1,
      maximumQuantity: null,
      discountType: 'percentage',
      discountValue: 0,
      effectiveFrom: '',
      effectiveTo: '',
      isActive: true,
      priority: 0,
    },
  });

  // Populate form when data loads
  useEffect(() => {
    if (pricingTier) {
      reset({
        tierName: pricingTier.tierName,
        minimumQuantity: pricingTier.minimumQuantity,
        maximumQuantity: pricingTier.maximumQuantity || null,
        discountType: pricingTier.discountType,
        discountValue: pricingTier.discountValue,
        effectiveFrom: pricingTier.effectiveFrom ? pricingTier.effectiveFrom.split('T')[0] : '',
        effectiveTo: pricingTier.effectiveTo ? pricingTier.effectiveTo.split('T')[0] : '',
        isActive: pricingTier.isActive,
        priority: pricingTier.priority,
      });

      if (pricingTier.customer) {
        setSelectedCustomer(pricingTier.customer);
      }
      if (pricingTier.product) {
        setSelectedProduct(pricingTier.product);
      }
    }
  }, [pricingTier, reset]);

  const discountType = watch('discountType');

  const updateMutation = useMutation({
    mutationFn: (data: UpdatePricingTierDto) => axiosInstance.patch(`/pricing-tiers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-tiers'] });
      queryClient.invalidateQueries({ queryKey: ['pricing-tier', id] });
      showMessage('Pricing tier updated successfully', 'success');
      navigate('/app/pricing-tiers');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update pricing tier', 'error');
    },
  });

  const onSubmit = (data: FormData) => {
    const payload: UpdatePricingTierDto = {
      customerId: selectedCustomer?.id || undefined,
      productId: selectedProduct?.id || undefined,
      tierName: data.tierName,
      minimumQuantity: Number(data.minimumQuantity),
      maximumQuantity: data.maximumQuantity ? Number(data.maximumQuantity) : undefined,
      discountType: data.discountType,
      discountValue: Number(data.discountValue),
      effectiveFrom: data.effectiveFrom || undefined,
      effectiveTo: data.effectiveTo || undefined,
      isActive: data.isActive,
      priority: Number(data.priority),
    };

    updateMutation.mutate(payload);
  };

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: colors.surfaceElevated,
      borderRadius: theme.radius.sm,
      '& fieldset': {
        borderColor: colors.border,
      },
      '&:hover fieldset': {
        borderColor: palette.primary,
      },
      '&.Mui-focused fieldset': {
        borderColor: palette.primary,
      },
    },
    '& .MuiInputLabel-root': {
      color: colors.textMuted,
    },
    '& .MuiInputBase-input': {
      color: colors.textPrimary,
    },
  };

  if (tierLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: palette.primary }} />
      </Box>
    );
  }

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/pricing-tiers')}
          sx={{
            color: colors.textMuted,
            mb: 2,
            '&:hover': {
              color: palette.primary,
            },
          }}
        >
          Back to Pricing Tiers
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LocalOfferIcon sx={{ color: palette.primary, fontSize: 24 }} />
          <Typography
            variant="overline"
            sx={{
              color: palette.primary,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            B2B PRICING SYSTEM
          </Typography>
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.extrabold,
            color: colors.textPrimary,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          EDIT PRICING TIER
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card
          sx={{
            backgroundColor: colors.surface,
            border: `2px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
            p: 4,
            ...animations.slideUp(0.1),
          }}
        >
          {/* Tier Configuration */}
          <Typography
            variant="h6"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              mb: 3,
              pb: 1,
              borderBottom: `2px solid ${colors.border}`,
            }}
          >
            TIER CONFIGURATION
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
            {/* Tier Name */}
            <Controller
              name="tierName"
              control={control}
              rules={{ required: 'Tier name is required' }}
              render={({ field }) => (
                <FormControl fullWidth sx={inputStyles}>
                  <InputLabel>Tier Name</InputLabel>
                  <Select {...field} label="Tier Name">
                    <MenuItem value="bronze">Bronze</MenuItem>
                    <MenuItem value="silver">Silver</MenuItem>
                    <MenuItem value="gold">Gold</MenuItem>
                    <MenuItem value="platinum">Platinum</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              )}
            />

            {/* Priority */}
            <TextField
              {...register('priority', { valueAsNumber: true })}
              label="Priority"
              type="number"
              fullWidth
              helperText="Higher priority tiers are applied first"
              sx={inputStyles}
            />
          </Box>

          {/* Target Selection */}
          <Typography
            variant="h6"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              mb: 3,
              pb: 1,
              borderBottom: `2px solid ${colors.border}`,
            }}
          >
            TARGET (OPTIONAL)
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
            {/* Customer Selector */}
            <Autocomplete
              options={customers || []}
              loading={customersLoading}
              value={selectedCustomer}
              onChange={(_, value) => setSelectedCustomer(value)}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer (leave empty for all)"
                  sx={inputStyles}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {customersLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Product Selector */}
            <Autocomplete
              options={products || []}
              loading={productsLoading}
              value={selectedProduct}
              onChange={(_, value) => setSelectedProduct(value)}
              getOptionLabel={(option) => `${option.name} (${option.sku})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Product (leave empty for all)"
                  sx={inputStyles}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {productsLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Quantity Range */}
          <Typography
            variant="h6"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              mb: 3,
              pb: 1,
              borderBottom: `2px solid ${colors.border}`,
            }}
          >
            QUANTITY RANGE
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
            <TextField
              {...register('minimumQuantity', {
                required: 'Minimum quantity is required',
                min: { value: 1, message: 'Minimum must be at least 1' },
                valueAsNumber: true,
              })}
              label="Minimum Quantity"
              type="number"
              fullWidth
              error={!!errors.minimumQuantity}
              helperText={errors.minimumQuantity?.message}
              sx={inputStyles}
            />

            <TextField
              {...register('maximumQuantity', { valueAsNumber: true })}
              label="Maximum Quantity (leave empty for unlimited)"
              type="number"
              fullWidth
              sx={inputStyles}
            />
          </Box>

          {/* Discount Configuration */}
          <Typography
            variant="h6"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              mb: 3,
              pb: 1,
              borderBottom: `2px solid ${colors.border}`,
            }}
          >
            DISCOUNT
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
            <Controller
              name="discountType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth sx={inputStyles}>
                  <InputLabel>Discount Type</InputLabel>
                  <Select {...field} label="Discount Type">
                    <MenuItem value="percentage">Percentage (%)</MenuItem>
                    <MenuItem value="fixed">Fixed Amount (DZD)</MenuItem>
                  </Select>
                </FormControl>
              )}
            />

            <TextField
              {...register('discountValue', {
                required: 'Discount value is required',
                min: { value: 0, message: 'Discount must be positive' },
                max: discountType === 'percentage' ? { value: 100, message: 'Percentage cannot exceed 100%' } : undefined,
                valueAsNumber: true,
              })}
              label={discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount (DZD)'}
              type="number"
              fullWidth
              error={!!errors.discountValue}
              helperText={errors.discountValue?.message}
              sx={inputStyles}
            />
          </Box>

          {/* Effective Dates */}
          <Typography
            variant="h6"
            sx={{
              fontFamily: typography.fontFamily.display,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              mb: 3,
              pb: 1,
              borderBottom: `2px solid ${colors.border}`,
            }}
          >
            EFFECTIVE PERIOD (OPTIONAL)
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
            <TextField
              {...register('effectiveFrom')}
              label="Effective From"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={inputStyles}
            />

            <TextField
              {...register('effectiveTo')}
              label="Effective To"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={inputStyles}
            />
          </Box>

          {/* Status */}
          <Box sx={{ mb: 4 }}>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: palette.success,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: palette.success,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>
                      Active
                    </Typography>
                  }
                />
              )}
            />
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/app/pricing-tiers')}
              sx={{
                borderColor: colors.border,
                color: colors.textPrimary,
                '&:hover': {
                  borderColor: colors.textMuted,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={updateMutation.isPending}
              sx={{
                backgroundColor: palette.primary,
                color: '#FFFFFF',
                fontWeight: typography.fontWeight.bold,
                '&:hover': {
                  backgroundColor: alpha(palette.primary, 0.8),
                },
              }}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Card>
      </form>
    </Box>
  );
};
